import {
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { Mode } from "../../components/ReadWriteTextField"
import { useEffect, useState } from "react"
import { SectionHead } from "./SectionHead"
import { RelationshipOptions } from "../flows/OnBoarding/RelationshipScreen"
import { PROBATE_IDS } from "../../constants"
import { User } from "../../models/User"
import { UserProfileAPI } from "../../apis/UserProfileAPI"
import { UserProfile } from "../../models/UserProfile"
import { UserAPI } from "../../apis/UserAPI"
import { ReadWriteWrapper } from "../../components/ReadWriteWrapper"
import { AddressTextField } from "../../components/AddressAutocompleteTextField2"
import { Place } from "../../models/Place"
import { intOrUndefined } from "../../utils/intOrUndefined"
import { SelectState } from "../../components/SelectState"
import { SelectCountry } from "../../components/SelectCountry"
import { useAuthContext } from "../../AuthContextWrapper"
import { useProbateContext } from "../../ProbateContextWrapper"
import React from "react"
import { useEstateUserContext } from "EstateUserContextWrapper"
import { Link } from "react-router-dom"
import { ExpandLess, ExpandMore } from "@mui/icons-material"
import { isValidDate } from "../../utils/isValidDate"
import DesktopDatePicker from "@mui/lab/DesktopDatePicker"
import { LocalizationProvider } from "@mui/lab"
import AdapterDateFns from "@mui/lab/AdapterDateFns"
import { parse } from "date-fns"
import { utcToZonedTime } from "date-fns-tz"

interface IProps {
  user: User
  setUser: (user: User) => void
  onSave?: (user: User) => void
  allowedModes?: Mode[]
  startMode?: Mode
  header?: string | JSX.Element
  headerIsUsername?: boolean
  clickHeaderToHideContents?: boolean
  autofocusFirstName?: boolean
  emailIsReadOnly?: boolean
  showRelationshipToDeceased?: boolean
  onCancelBeforeSave?: () => void
}
export function UserInfo(props: IProps): JSX.Element {
  const [userAtEditStart, setUserAtEditStart] = useState<User>(props.user)
  const [mode, setMode] = useState<Mode>(props.startMode || "read")
  const [error, setError] = useState<string>("")
  const [emailError, setEmailError] = useState<string>("")
  const [phoneError, setPhoneError] = useState<string>("")
  const [birthdayError, setBirthdayError] = useState<string>("")
  const [needManualAddress, setNeedManualAddress] = useState<boolean>(false)
  const [displayManualAddress, setDisplayManualAddress] = useState<boolean>(false)
  const [savedSuccessfully, setSavedSuccessfully] = useState<boolean | null>(null)
  const [relationshipToDeceased, setRelationshipToDeceased] = useState<string>("")
  const [relationshipToDeceasedAtEditStart, setRelationshipToDeceasedAtEditStart] =
    useState<string>("")
  const [header, setHeader] = useState<string | JSX.Element | undefined>(props.header)
  const [haveSaved, setHaveSaved] = useState<boolean>(false)
  const [contentsVisible, setContentsVisible] = useState<boolean>(true)
  const { getFieldAnswerForUser, storeFieldAnswered } = useProbateContext()
  const authParams = useAuthContext()
  const states = authParams.states
  const countries = authParams.countries
  const authedUser = authParams.user
  const activeEstate = authParams.activeEstate

  function canEdit(): boolean {
    if (!props.allowedModes) {
      return true
    }

    return !!props.allowedModes?.find((m) => m === "edit")
  }

  useEffect(() => {
    async function getKnowledgeFieldAnswers() {
      if (!authedUser || !props.user) {
        return
      }

      let answerValue: string | boolean | number | undefined = undefined
      if (authedUser.id && props.user.id == authedUser.id) {
        answerValue = await getFieldAnswerForUser(authedUser.id, PROBATE_IDS.relationshipToDeceased)
      } else {
        if (!props.user.id) {
          answerValue = relationshipToDeceased
        } else {
          // Disabled until we can actually update field answers for other users in the knowledge engine
          // answerValue = await getFieldAnswerForOtherUser(props.user.id, PROBATE_IDS.relationshipToDeceased)
        }
      }

      if (typeof answerValue === "string") {
        setRelationshipToDeceased(answerValue)
      }
    }
    getKnowledgeFieldAnswers()
  }, [authedUser?.id, props.user?.id])

  function addressFieldKey(): string {
    if (!props.user.profile) {
      return "addressfieldkey" + props.user.email + props.user.phone
    }

    const p = props.user.profile
    return (
      "addressfieldkey" +
      p.firstName +
      "_" +
      p.middleName +
      "_" +
      p.lastName +
      "_" +
      props.user.email +
      props.user.phone
    )
  }

  function validate(): boolean {
    if (!props.user) {
      return false
    }

    setEmailError("")
    setPhoneError("")
    setBirthdayError("")

    let hasError = false
    if (
      props.user.email &&
      (props.user.email.indexOf("@") == -1 || props.user.email.indexOf(".") == -1)
    ) {
      setEmailError("Invalid email address")
      hasError = true
    }

    const allowedPhoneChars = [" ", "+", "-", "(", ")"]
    if (props.user.phone && props.user.phone.length > 0) {
      let numDigits = 0
      for (let i = 0; i < props.user.phone.length; i++) {
        if (!isNaN(parseInt(props.user.phone[i]))) {
          numDigits++
        } else if (allowedPhoneChars.indexOf(props.user.phone[i]) === -1) {
          setPhoneError("Invalid characters found in phone number")
          hasError = true
        }
      }
      if (numDigits < 10) {
        setPhoneError("Please enter a valid phone number including area code")
        hasError = true
      }
    }

    console.log("props.user.profile?.birthdate", props.user.profile?.birthdate)
    if (props.user.profile?.birthdate && !isValidDate(props.user.profile.birthdate)) {
      setBirthdayError(`${props.user.profile?.birthdate} is not a valid date`)
      hasError = true
    }

    return !hasError
  }

  async function save() {
    if (!authedUser?.id || !props.user) {
      setError("not done loading user(s) yet")
      return
    }

    if (!validate()) {
      console.warn("form errors, not saving")
      return
    }

    try {
      setError("")
      setSavedSuccessfully(null)
      setDisplayManualAddress(false)
      setNeedManualAddress(false)

      const profileJson = props.user.profile?.toJSON() || new UserProfile().toJSON()
      profileJson.id = props.user.profile?.id || undefined

      if (authedUser.id == props.user.id) {
        // Store everything async for the logged in user
        await Promise.all([
          storeFieldAnswered({
            id: PROBATE_IDS.relationshipToDeceased,
            answer: relationshipToDeceased || undefined,
            answerValue: relationshipToDeceased || undefined,
            prefix: "",
            getOptionLabel: () => undefined,
          }),
          UserProfileAPI.update(authedUser.profile?.toJSON() || {}),
          UserAPI.updatePhone(authedUser.id, props.user.phone || null),
        ])
      } else {
        // Store other user's info async
        let myUser: User | undefined = props.user
        if (!myUser.id && activeEstate?.id) {
          // Create a new user.  This will add them to the estate as well
          myUser = await UserAPI.createOtherUser(activeEstate.id, myUser)
          if (!myUser || !myUser.id) {
            throw new Error("unable to create new user in this estate")
          }
        }

        profileJson.id = myUser.profile?.id
        profileJson.phone = profileJson.phone || myUser.phone
        profileJson.user_id = myUser.id

        await Promise.all([
          UserProfileAPI.update(profileJson),

          // Disabled until we can actually update field answers for other users in the knowledge engine
          // storeFieldAnsweredForOtherUser(myUser.id, {
          //   id: PROBATE_IDS.relationshipToDeceased,
          //   answer: relationshipToDeceased || undefined,
          //   answerValue: relationshipToDeceased || undefined,
          //   prefix: "",
          //   getOptionLabel: () => undefined,
          // }),
        ])
      }

      const profileClone = UserProfile.fromJSON(profileJson) || new UserProfile()
      profileClone.id = profileJson.id

      const userClone = User.fromJSON(props.user.toJson())
      userClone.id = props.user.id
      userClone.profile = profileClone
      userClone.countryId = intOrUndefined(profileJson.countries_id)
      props.setUser(userClone)

      if (props.onSave) {
        props.onSave(userClone)
      }

      setHaveSaved(true)
      setSavedSuccessfully(true)
      setUserAtEditStart(props.user)
      setRelationshipToDeceasedAtEditStart(relationshipToDeceased)
      setMode("read")
      setError("")
    } catch (e) {
      setError("unable to save estate: " + (e as any)?.message)
    }
  }

  function headerClickWrapper(child: string | JSX.Element) {
    if (!props.clickHeaderToHideContents) {
      return child
    }

    return (
      <Link
        to={""}
        color="secondary"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()

          if (mode == "edit") {
            return
          }

          setContentsVisible(!contentsVisible)
        }}
      >
        <Typography color="secondary">
          <u style={{ whiteSpace: "nowrap", display: "flex", alignItems: "center" }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {child}
            </div>
            {contentsVisible ? <ExpandLess /> : <ExpandMore />}
          </u>
        </Typography>
      </Link>
    )
  }

  useEffect(() => {
    if (!props.user) {
      return
    }
    if (!props.headerIsUsername) {
      return
    }

    setHeader(
      <Typography
        variant="h3"
        color="secondary"
        style={{
          fontSize: "16pt",
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {getUsername()}
      </Typography>
    )
  }, [props.user, props.user?.first, props.user?.middle, props.user?.last])

  function getUsername(): string {
    return [
      props.user?.profile?.firstName || "",
      props.user?.profile?.middleName || "",
      props.user?.profile?.lastName || "",
    ]
      .join(" ")
      .trim()
  }

  return (
    <div key={mode + " " + props.user?.id}>
      <form>
        <SectionHead
          mode={mode}
          canEdit={canEdit()}
          header={headerClickWrapper(header || "")}
          message={
            savedSuccessfully ? (
              <Typography variant="body1" color="primary">
                Saved successfully
              </Typography>
            ) : error ? (
              <Typography variant="body1" color="error">
                {error}
              </Typography>
            ) : (
              <></>
            )
          }
          onCancel={() => {
            setMode("read")
            setEmailError("")
            setPhoneError("")
            setDisplayManualAddress(false)
            setNeedManualAddress(false)
            setSavedSuccessfully(null)
            props.setUser(userAtEditStart)
            setRelationshipToDeceased(relationshipToDeceasedAtEditStart)
            if (!!props.onCancelBeforeSave && !haveSaved) {
              props.onCancelBeforeSave()
            }
          }}
          onEdit={() => {
            if (!props.user) {
              setError("not yet done loading my user ")
              return
            }
            setContentsVisible(true)

            const userClone = User.fromJSON(props.user.toJson())
            const profileClone =
              UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
            profileClone.id = props.user.profile?.id
            userClone.id = props.user.id
            userClone.profile = profileClone
            setUserAtEditStart(userClone)

            setSavedSuccessfully(null)
            setRelationshipToDeceasedAtEditStart(relationshipToDeceased)
            setMode("edit")
          }}
          onSave={() => save()}
        />
        {(contentsVisible || mode === "edit") && (
          <>
            <Grid container style={{ marginTop: "1rem" }}>
              <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{ paddingRight: "1rem" }}>
                <Typography
                  variant="body1"
                  component="div"
                  style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                >
                  First Name
                </Typography>
                <ReadWriteWrapper
                  value={props.user.profile?.firstName || ""}
                  mode={mode}
                  blankReadOnlyValue="--"
                >
                  <TextField
                    value={props.user.profile?.firstName || ""}
                    size="small"
                    autoFocus={props.autofocusFirstName}
                    fullWidth
                    onChange={(e) => {
                      if (!props.user) {
                        return
                      }

                      const profileClone =
                        UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                      profileClone.id = props.user.profile?.id
                      profileClone.firstName = e.target.value

                      const userClone = User.fromJSON(props.user.toJson())
                      userClone.id = props.user.id
                      userClone.profile = profileClone

                      props.setUser(userClone)
                    }}
                  />
                </ReadWriteWrapper>
              </Grid>
              <Grid item xs={12} sm={3} md={3} lg={3} xl={3} style={{ paddingRight: "1rem" }}>
                <Typography
                  variant="body1"
                  component="div"
                  style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                >
                  Middle Name
                </Typography>
                <ReadWriteWrapper
                  value={props.user.profile?.middleName || ""}
                  mode={mode}
                  blankReadOnlyValue="--"
                >
                  <TextField
                    value={props.user.profile?.middleName || ""}
                    size="small"
                    fullWidth
                    onChange={(e) => {
                      if (!props.user) {
                        return
                      }

                      const clone =
                        UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                      clone.id = props.user.profile?.id
                      clone.middleName = e.target.value

                      const userClone = User.fromJSON(props.user.toJson())
                      userClone.id = props.user.id
                      userClone.profile = clone
                      props.setUser(userClone)
                    }}
                  />
                </ReadWriteWrapper>
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{ paddingRight: "1rem" }}>
                <Typography
                  variant="body1"
                  component="div"
                  style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                >
                  Last Name
                </Typography>
                <ReadWriteWrapper
                  value={props.user.profile?.lastName || ""}
                  mode={mode}
                  blankReadOnlyValue="--"
                >
                  <TextField
                    value={props.user.profile?.lastName || ""}
                    fullWidth
                    size="small"
                    onChange={(e) => {
                      if (!props.user) {
                        return
                      }

                      const clone =
                        UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                      clone.id = props.user.profile?.id
                      clone.lastName = e.target.value

                      const userClone = User.fromJSON(props.user.toJson())
                      userClone.id = props.user.id
                      userClone.profile = clone
                      props.setUser(userClone)
                    }}
                  />
                </ReadWriteWrapper>
              </Grid>
            </Grid>

            <Grid container style={{ marginTop: "1rem" }}>
              <Grid item xs={12} sm={6} md={6} lg={6} xl={6} style={{ paddingRight: "1rem" }}>
                <Typography
                  variant="body1"
                  component="div"
                  style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                >
                  Email Address
                </Typography>
                <ReadWriteWrapper
                  value={props.user?.email || ""}
                  mode={props.emailIsReadOnly ? "read" : mode} // for users that can't login, we are able to update email
                  blankReadOnlyValue="--"
                >
                  <TextField
                    value={props.user?.email || ""}
                    fullWidth
                    size="small"
                    required={true}
                    onChange={(e) => {
                      if (!props.user) {
                        return
                      }

                      const userClone = User.fromJSON(props.user.toJson())
                      userClone.id = props.user.id
                      userClone.email = e.target.value
                      props.setUser(userClone)
                    }}
                    onKeyUp={() => validate()}
                  />
                </ReadWriteWrapper>
                {emailError && (
                  <Typography component="div" color="error">
                    {emailError}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Grid container style={{ marginTop: "1rem" }}>
              <Grid item xs={12} sm={6} md={6} lg={6} xl={6} style={{ paddingRight: "1rem" }}>
                <Typography
                  variant="body1"
                  component="div"
                  style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                >
                  Phone number
                </Typography>

                <ReadWriteWrapper
                  value={props.user?.phone || ""}
                  mode={mode}
                  blankReadOnlyValue="--"
                >
                  <TextField
                    value={props.user?.phone || ""}
                    fullWidth
                    size="small"
                    required={true}
                    onChange={(e) => {
                      if (!props.user) {
                        return
                      }

                      const userClone = User.fromJSON(props.user.toJson())
                      userClone.id = props.user.id
                      userClone.phone = e.target.value
                      props.setUser(userClone)
                    }}
                    onKeyUp={() => validate()}
                  />
                </ReadWriteWrapper>
                {phoneError && (
                  <Typography component="div" color="error">
                    {phoneError}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Grid container style={{ marginTop: "1rem" }}>
              <Grid item xs={12} sm={8} md={8} lg={6} xl={6} style={{ paddingRight: "1rem" }}>
                <Typography
                  variant="body1"
                  component="div"
                  style={{
                    marginBottom: "0.5rem",
                    fontWeight: "bold",
                    color: displayManualAddress ? "#888" : undefined,
                  }}
                >
                  Address
                </Typography>
                {!(!!props.user.profile && countries && states) ? (
                  <></>
                ) : (
                  <ReadWriteWrapper
                    value={props.user.profile?.fullAddress(countries, states) || ""}
                    mode={mode}
                    blankReadOnlyValue="--"
                  >
                    <AddressTextField
                      key={addressFieldKey() + mode} // this needs to be re-rendered via this key every time a relevant profile field changes, otherwise we will reset the profile props to what they were when it was initially rendered
                      disabled={displayManualAddress}
                      initAddress={props.user.profile?.fullAddress(countries, states) || ""}
                      onChange={async (
                        ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                      ) => {
                        if (!props.user) {
                          return
                        }

                        setDisplayManualAddress(false)
                        setNeedManualAddress(true)

                        const userProfileClone =
                          UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                        userProfileClone.id = props.user.profile?.id
                        userProfileClone.addressOne = ev.target.value
                        userProfileClone.addressTwo = null
                        userProfileClone.stateId = null
                        userProfileClone.countryId = null
                        userProfileClone.city = null
                        userProfileClone.zipCode = null

                        const userClone = User.fromJSON(props.user.toJson())
                        userClone.id = props.user.id
                        userClone.profile = userProfileClone
                        props.setUser(userClone)
                      }}
                      onComplete={async (place: Place) => {
                        if (!props.user) {
                          return
                        }

                        setDisplayManualAddress(false)
                        setNeedManualAddress(false)

                        const userProfileClone =
                          UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                        userProfileClone.id = props.user.profile?.id
                        userProfileClone.addressOne = (
                          (place?.number || "") +
                          " " +
                          (place?.street || "")
                        ).trim()
                        userProfileClone.addressTwo = place?.address2
                        userProfileClone.stateId = states
                          ?.find(
                            (s) =>
                              s.fullName?.toLowerCase() == place?.state?.toLowerCase() ||
                              s.code?.toLowerCase() == place?.state?.toLowerCase()
                          )
                          ?.id?.toString()
                        userProfileClone.countryId = countries
                          ?.find(
                            (c) =>
                              c.fullName?.toLowerCase() == place.country?.toLowerCase() ||
                              c.code?.toLowerCase() == place.country?.toLowerCase() ||
                              (c.code?.toLowerCase() == "usa" &&
                                place.country?.toLowerCase() == "us")
                          )
                          ?.id?.toString()
                        userProfileClone.city = place?.city
                        userProfileClone.zipCode = place?.postal_code

                        const userClone = User.fromJSON(props.user.toJson())
                        userClone.id = props.user.id
                        userClone.profile = userProfileClone
                        props.setUser(userClone)
                      }}
                      onClear={function (): void {
                        if (!props.user) {
                          return
                        }

                        const userProfileClone =
                          UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                        userProfileClone.id = props.user.profile?.id
                        userProfileClone.addressOne = null
                        userProfileClone.addressTwo = null
                        userProfileClone.stateId = null
                        userProfileClone.countryId = null
                        userProfileClone.zipCode = null

                        const userClone = User.fromJSON(props.user.toJson())
                        userClone.id = props.user.id
                        userClone.profile = userProfileClone
                        props.setUser(userClone)
                      }}
                    />
                    {!displayManualAddress && needManualAddress ? (
                      <Typography
                        component="div"
                        variant="caption"
                        style={{ color: displayManualAddress ? "#888" : undefined }}
                      >
                        We can’t seem to find this address. Please try again or
                        <Link
                          to={""}
                          style={{ marginLeft: "5px", marginRight: "5px" }}
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setDisplayManualAddress(true)
                          }}
                        >
                          add it manually.
                        </Link>
                      </Typography>
                    ) : (
                      <></>
                    )}
                  </ReadWriteWrapper>
                )}
              </Grid>
            </Grid>
            {displayManualAddress && (
              <>
                <Grid container style={{ marginTop: "1rem" }}>
                  <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{ paddingRight: "1rem" }}>
                    <Typography
                      variant="body1"
                      component="div"
                      style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                    >
                      Street address
                    </Typography>
                    <ReadWriteWrapper
                      value={props.user.profile?.addressOne || ""}
                      mode={mode}
                      blankReadOnlyValue="--"
                    >
                      <TextField
                        value={props.user.profile?.addressOne || ""}
                        fullWidth
                        size="small"
                        onChange={(e) => {
                          if (!props.user) {
                            return
                          }

                          const userProfileClone =
                            UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                          userProfileClone.id = props.user.profile?.id
                          userProfileClone.addressOne = e.target.value

                          const userClone = User.fromJSON(props.user.toJson())
                          userClone.id = props.user.id
                          userClone.profile = userProfileClone
                          props.setUser(userClone)
                        }}
                      />
                    </ReadWriteWrapper>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} lg={3} xl={3} style={{ paddingRight: "1rem" }}>
                    <Typography
                      variant="body1"
                      component="div"
                      style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                    >
                      Apt
                    </Typography>
                    <ReadWriteWrapper
                      value={props.user.profile?.addressTwo || ""}
                      mode={mode}
                      blankReadOnlyValue="--"
                    >
                      <TextField
                        value={props.user.profile?.addressTwo || ""}
                        fullWidth
                        size="small"
                        onChange={(e) => {
                          if (!props.user) {
                            return
                          }

                          const userProfileClone =
                            UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                          userProfileClone.id = props.user.profile?.id
                          userProfileClone.addressTwo = e.target.value

                          const userClone = User.fromJSON(props.user.toJson())
                          userClone.id = props.user.id
                          userClone.profile = userProfileClone
                          props.setUser(userClone)
                        }}
                      />
                    </ReadWriteWrapper>
                  </Grid>
                </Grid>
                <Grid container style={{ marginTop: "1rem" }}>
                  <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{ paddingRight: "1rem" }}>
                    <Typography
                      variant="body1"
                      component="div"
                      style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                    >
                      City
                    </Typography>
                    <ReadWriteWrapper
                      value={props.user.profile?.city || ""}
                      mode={mode}
                      blankReadOnlyValue="--"
                    >
                      <TextField
                        value={props.user.profile?.city || ""}
                        fullWidth
                        size="small"
                        onChange={(e) => {
                          if (!props.user) {
                            return
                          }

                          const userProfileClone =
                            UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                          userProfileClone.id = props.user.profile?.id
                          userProfileClone.city = e.target.value

                          const userClone = User.fromJSON(props.user.toJson())
                          userClone.id = props.user.id
                          userClone.profile = userProfileClone
                          props.setUser(userClone)
                        }}
                      />
                    </ReadWriteWrapper>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} lg={3} xl={3} style={{ paddingRight: "1rem" }}>
                    <Typography
                      variant="body1"
                      component="div"
                      style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                    >
                      State
                    </Typography>
                    <ReadWriteWrapper
                      value={
                        states?.find((s) => s.id.toString() == props.user.profile?.stateId)
                          ?.fullName || ""
                      }
                      mode={mode}
                      blankReadOnlyValue="--"
                    >
                      <SelectState
                        stateId={props.user.profile?.stateId || ""}
                        size="small"
                        allowedCountryIds={
                          props.user.profile?.countryId
                            ? [parseInt(props.user.profile?.countryId)]
                            : undefined
                        }
                        onStateIdChange={(id: string) => {
                          if (!props.user) {
                            return
                          }

                          const userProfileClone =
                            UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                          userProfileClone.id = props.user.profile?.id
                          userProfileClone.stateId = id
                          if (!userProfileClone.countryId) {
                            userProfileClone.countryId = states
                              ?.find((s) => s.id.toString() == id)
                              ?.countryId.toString()
                          }

                          const userClone = User.fromJSON(props.user.toJson())
                          userClone.id = props.user.id
                          userClone.profile = userProfileClone
                          props.setUser(userClone)
                        }}
                      />
                    </ReadWriteWrapper>
                  </Grid>
                  <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{ paddingRight: "1rem" }}>
                    <Typography
                      variant="body1"
                      component="div"
                      style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                    >
                      Country
                    </Typography>
                    <ReadWriteWrapper
                      value={
                        countries?.find((c) => c.id.toString() == props.user.profile?.countryId)
                          ?.fullName || ""
                      }
                      mode={mode}
                      blankReadOnlyValue="--"
                    >
                      <SelectCountry
                        countryId={props.user.profile?.countryId || ""}
                        size="small"
                        onCountryIdChange={(id: string) => {
                          if (!props.user) {
                            return
                          }

                          const userProfileClone =
                            UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                          userProfileClone.id = props.user.profile?.id
                          userProfileClone.countryId = id

                          const userClone = User.fromJSON(props.user.toJson())
                          userClone.id = props.user.id
                          userClone.profile = userProfileClone
                          props.setUser(userClone)
                        }}
                      />
                    </ReadWriteWrapper>
                  </Grid>
                </Grid>
                <Grid container style={{ marginTop: "1rem" }}>
                  <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{ paddingRight: "1rem" }}>
                    <Typography
                      variant="body1"
                      component="div"
                      style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                    >
                      Postal Code
                    </Typography>
                    <ReadWriteWrapper
                      value={props.user.profile?.zipCode || ""}
                      mode={mode}
                      blankReadOnlyValue="--"
                    >
                      <TextField
                        value={props.user.profile?.zipCode || ""}
                        fullWidth
                        size="small"
                        onChange={(e) => {
                          if (!props.user) {
                            return
                          }

                          const userProfileClone =
                            UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                          userProfileClone.id = props.user.profile?.id
                          userProfileClone.zipCode = e.target.value

                          const userClone = User.fromJSON(props.user.toJson())
                          userClone.id = props.user.id
                          userClone.profile = userProfileClone
                          props.setUser(userClone)
                        }}
                      />
                    </ReadWriteWrapper>
                  </Grid>
                </Grid>
              </>
            )}
            <Grid container style={{ marginTop: "1rem" }}>
              <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{ paddingRight: "0.5rem" }}>
                <Typography
                  variant="body1"
                  component="div"
                  style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                >
                  Date of birth
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Stack spacing={3}>
                    <DesktopDatePicker
                      inputFormat="MM/dd/yyyy"
                      minDate={new Date("1900-01-01")}
                      maxDate={new Date()}
                      value={
                        props.user.profile?.birthdate
                          ? new Date(props.user.profile?.birthdate)
                          : null
                      }
                      readOnly={mode !== "edit"}
                      onChange={(date: Date | null, exactValue?: string) => {
                        if (!props.user) {
                          return
                        }

                        const userProfileClone =
                          UserProfile.fromJSON(props.user.profile?.toJSON()) || new UserProfile()
                        userProfileClone.id = props.user.profile?.id
                        userProfileClone.birthdate = date?.toISOString() || null

                        const userClone = User.fromJSON(props.user.toJson())
                        userClone.id = props.user.id
                        userClone.profile = userProfileClone
                        props.setUser(userClone)

                        return true
                      }}
                      renderInput={(params) => {
                        return (
                          <ReadWriteWrapper
                            value={
                              props.user.profile?.birthdate
                                ? new Date(props.user.profile?.birthdate).toLocaleDateString()
                                : ""
                            }
                            mode={mode}
                            blankReadOnlyValue="--"
                          >
                            <TextField
                              error={!!birthdayError}
                              value={props.user.profile?.birthdate || ""}
                              style={{
                                outlineColor: "red !important",
                                borderColor: "red !important",
                                outlineWidth: "2",
                              }}
                              size="small"
                              fullWidth
                              {...params}
                            />
                          </ReadWriteWrapper>
                        )
                      }}
                    />
                  </Stack>
                </LocalizationProvider>
                {birthdayError && (
                  <Typography component="div" color="error">
                    {birthdayError}
                  </Typography>
                )}
              </Grid>
            </Grid>
            {props.showRelationshipToDeceased && (
              <Grid container style={{ marginTop: "1rem" }}>
                <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
                  <Typography
                    variant="body1"
                    component="div"
                    style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
                  >
                    Relationship to deceased
                  </Typography>
                  <ReadWriteWrapper
                    value={relationshipToDeceased}
                    mode={mode}
                    blankReadOnlyValue="--"
                  >
                    <Select
                      size="small"
                      readOnly={mode !== "edit"}
                      fullWidth
                      onChange={(e: SelectChangeEvent) => {
                        setRelationshipToDeceased(e.target.value.toString())
                        return true
                      }}
                      value={relationshipToDeceased}
                    >
                      {RelationshipOptions.map((option, index) => {
                        return (
                          <MenuItem key={"option" + index} value={option.value.toString()}>
                            {option.label}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </ReadWriteWrapper>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </form>
    </div>
  )
}
